import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle, KeyRound, Lock, Unlock, CheckCircle2, Activity,
  Calendar, TrendingDown, TrendingUp, Minus, Search, ChevronLeft, ChevronRight,
  X as XIcon, Plus,
} from "lucide-react";

import {
  fetchCrawlerCategories, createAuthProfile,
  type ChannelCard, type PendingAction,
} from "../api/crawlerOps";
import { explainError } from "../api/client";
import { Spinner } from "../components/Spinner";
import { shortDate } from "../lib/format";

/* ──────────────────────────────────────────────────────────────────────
   /crawler-ops/publishers — full-catalog directory + ops view.

   ONE page covering every publisher in our catalog (1500+ rows). Each row
   declares its onboarding state (crawling / auth_ready / auth_partial /
   catalog) and exposes the appropriate next action:
     · crawling           → Re-login (if auth) · Run (placeholder)
     · auth_ready         → Navigate · Run (placeholder; recipe TBD)
     · auth_partial       → Login (to capture cookies)
     · catalog (untouched)→ Set up login (create AuthProfile)

   Filters: category tabs · state chip · text search · pagination. Designed
   for an ops team to divide login work between crawling-team and pre-onboard
   team.
   ────────────────────────────────────────────────────────────────────── */

const CATEGORY_ORDER = ["Financial", "Mainline", "Regional", "Periodical", "Other"] as const;

interface UiFilters {
  category: string | null;     // null = all
  state:    string | null;     // null = all
  q:        string;
  page:     number;
}

const DEFAULT_FILTERS: UiFilters = {
  category: null, state: null, q: "", page: 1,
};

export function CrawlerPublishers() {
  const [filters, setFilters] = useState<UiFilters>(DEFAULT_FILTERS);
  const [debouncedQ, setDebouncedQ] = useState(filters.q);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q), 250);
    return () => clearTimeout(t);
  }, [filters.q]);

  const q = useQuery({
    queryKey: ["crawler/categories", filters.category, filters.state, debouncedQ, filters.page],
    queryFn:  () => fetchCrawlerCategories({
      category:  filters.category ?? undefined,
      state:     filters.state ?? undefined,
      q:         debouncedQ || undefined,
      page:      filters.page,
      page_size: 50,
    }),
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
  });

  // Default the active category to whichever has the most channels — once.
  const defaultCatPicked = useMemo(() => filters.category !== null, [filters.category]);
  useEffect(() => {
    if (!defaultCatPicked && q.data && q.data.counts_by_category.length > 0) {
      // Don't auto-jump the user — keep "All" as the initial default so they see counts.
    }
  }, [q.data, defaultCatPicked]);

  const totalPages = q.data ? Math.max(1, Math.ceil(q.data.total_filtered / q.data.page_size)) : 1;

  const [loginModal, setLoginModal] = useState<ChannelCard | null>(null);

  function applyFilter(patch: Partial<UiFilters>) {
    setFilters((f) => ({ ...f, ...patch, page: patch.page ?? 1 }));
  }

  return (
    <div className="p-6 max-w-[1100px] mx-auto">
      <header className="mb-5">
        <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">
          Crawler · Publishers
        </div>
        <h1 className="font-display text-[28px] leading-tight">Publishers</h1>
        <p className="font-sans text-[13px] text-[var(--fg-muted)] mt-1 max-w-[720px]">
          Every publisher in our catalog. Crawling rows show today's health; catalog rows
          are ready to be claimed for login work — divide the next batch between teams
          using the state filter.
        </p>
      </header>

      {q.error && (
        <div className="border rounded p-3 text-[13px] mb-3"
             style={{ borderColor: "var(--rule)", color: "var(--fg-danger)" }}>
          <AlertTriangle size={14} className="inline mr-1.5 align-text-bottom" />
          {explainError(q.error)}
        </div>
      )}

      {/* Pending banner — only if anything's actionable */}
      {q.data && <PendingActionsBanner pending={q.data.pending} />}

      {/* Filter bar: search + state chips */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center border rounded px-2 py-1 flex-1 min-w-[260px]"
             style={{ borderColor: "var(--rule)" }}>
          <Search size={14} className="text-[var(--fg-faint)] mr-1.5" />
          <input
            value={filters.q}
            onChange={(e) => applyFilter({ q: e.target.value })}
            placeholder="Search publisher name…"
            className="bg-transparent outline-none flex-1 font-sans text-[13px]"
          />
          {filters.q && (
            <button onClick={() => applyFilter({ q: "" })} aria-label="clear search">
              <XIcon size={14} className="text-[var(--fg-faint)]" />
            </button>
          )}
        </div>
        <StateChips state={filters.state} onChange={(s) => applyFilter({ state: s })} />
      </div>

      {/* Category tabs */}
      {q.data && (
        <div className="mt-4 mb-4 border-b" style={{ borderColor: "var(--rule)" }}>
          <div className="flex gap-1 flex-wrap">
            <CategoryTab
              label="All"
              count={q.data.counts_by_category.reduce((s, c) => s + c.total, 0)}
              active={filters.category === null}
              onClick={() => applyFilter({ category: null })}
            />
            {CATEGORY_ORDER.filter((c) => q.data!.counts_by_category.some((x) => x.category === c)).map((c) => {
              const cnt = q.data!.counts_by_category.find((x) => x.category === c)?.total ?? 0;
              return (
                <CategoryTab
                  key={c} label={c} count={cnt}
                  active={filters.category === c}
                  onClick={() => applyFilter({ category: c })}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* List */}
      {q.isLoading && !q.data && (
        <div className="flex items-center gap-2 text-[var(--fg-muted)] py-4">
          <Spinner /> <span className="font-sans text-[13px]">Loading…</span>
        </div>
      )}
      {q.data && (
        <>
          {q.data.channels.length === 0 ? (
            <div className="font-sans text-[13px] text-[var(--fg-faint)] py-8 text-center">
              No publishers match these filters.
            </div>
          ) : (
            <div className="space-y-2.5">
              {q.data.channels.map((card) => (
                <ChannelRow
                  key={card.channel_id}
                  card={card}
                  onSetupLogin={() => setLoginModal(card)}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          <div className="mt-5 flex items-center justify-between font-mono text-[11px] text-[var(--fg-muted)]">
            <div>
              {q.data.total_filtered} publisher{q.data.total_filtered !== 1 ? "s" : ""}
              {" · "}page {q.data.page} of {totalPages}
            </div>
            <div className="flex gap-1">
              <button
                disabled={filters.page <= 1}
                onClick={() => applyFilter({ page: filters.page - 1 })}
                className="px-2 py-1 border rounded disabled:opacity-30"
                style={{ borderColor: "var(--rule)" }}>
                <ChevronLeft size={12} className="inline" /> Prev
              </button>
              <button
                disabled={filters.page >= totalPages}
                onClick={() => applyFilter({ page: filters.page + 1 })}
                className="px-2 py-1 border rounded disabled:opacity-30"
                style={{ borderColor: "var(--rule)" }}>
                Next <ChevronRight size={12} className="inline" />
              </button>
            </div>
          </div>

          <div className="mt-3 font-mono text-[10px] text-[var(--fg-faint)]">
            Signals computed against business date {q.data.business_date}.
          </div>
        </>
      )}

      {loginModal && (
        <SetupLoginModal
          channel={loginModal}
          onClose={() => setLoginModal(null)}
          onSuccess={() => {
            setLoginModal(null);
            q.refetch();
          }}
        />
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */

function StateChips({
  state, onChange,
}: { state: string | null; onChange: (s: string | null) => void; }) {
  const opts: Array<{ value: string | null; label: string }> = [
    { value: null,           label: "All states" },
    { value: "crawling",     label: "Crawling" },
    { value: "auth_ready",   label: "Auth captured" },
    { value: "auth_partial", label: "Auth partial" },
    { value: "catalog",      label: "Catalog only" },
  ];
  return (
    <div className="flex gap-1">
      {opts.map((o) => {
        const active = o.value === state;
        return (
          <button
            key={o.label}
            onClick={() => onChange(o.value)}
            className={[
              "px-2.5 py-1 border rounded font-mono text-[11px] transition-colors",
              active
                ? "bg-[var(--fg)] text-[var(--bg)] border-[var(--fg)]"
                : "hover:bg-[var(--bg-muted,#f4f0e6)]",
            ].join(" ")}
            style={!active ? { borderColor: "var(--rule)" } : undefined}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function CategoryTab({
  label, count, active, onClick,
}: { label: string; count: number; active: boolean; onClick: () => void; }) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-4 pt-2 pb-2 -mb-px border-b-2 font-display text-[15px] transition-colors",
        active
          ? "border-[var(--fg)] text-[var(--fg)]"
          : "border-transparent text-[var(--fg-muted)] hover:text-[var(--fg)]",
      ].join(" ")}>
      {label}
      <span className="ml-2 font-mono text-[11px] text-[var(--fg-faint)]">{count}</span>
    </button>
  );
}

/* ──────────────────────────────────────────────────────────────────── */

function PendingActionsBanner({ pending }: { pending: ReadonlyArray<PendingAction> }) {
  if (pending.length === 0) return null;
  const byKind = new Map<string, PendingAction[]>();
  for (const p of pending) {
    const arr = byKind.get(p.kind) ?? [];
    arr.push(p);
    byKind.set(p.kind, arr);
  }
  const KIND_META: Record<string, { label: string; tone: string }> = {
    relogin:             { label: "needs re-login",         tone: "danger" },
    cookies_expiring:    { label: "cookies expiring soon",   tone: "warn" },
    stuck:               { label: "no successful crawl",     tone: "warn" },
    reverify_regression: { label: "reverify regression",     tone: "warn" },
  };
  return (
    <div className="mt-1 border rounded p-3 space-y-1.5"
         style={{ borderColor: "var(--rule-strong, var(--rule))", background: "var(--bg-muted, transparent)" }}>
      <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-muted)]">
        Pending operator actions ({pending.length})
      </div>
      {Array.from(byKind.entries()).map(([kind, items]) => {
        const meta = KIND_META[kind] ?? { label: kind, tone: "warn" };
        return (
          <div key={kind} className="flex items-start gap-2 text-[13px] font-sans">
            <AlertTriangle
              size={14}
              className="mt-0.5"
              style={{ color: meta.tone === "danger" ? "var(--fg-danger)" : "var(--fg-warn, #b45309)" }}
            />
            <div className="flex-1">
              <span className="font-medium">{items.length} channel{items.length !== 1 ? "s" : ""} {meta.label}:</span>{" "}
              {items.map((it, i) => (
                <span key={it.channel_id}>
                  <Link
                    to={`/crawler-ops/sessions?channel=${it.channel_id}`}
                    className="underline hover:no-underline"
                  >
                    {it.channel}
                  </Link>
                  <span className="text-[var(--fg-faint)]"> ({it.detail})</span>
                  {i < items.length - 1 && <span className="text-[var(--fg-faint)]">, </span>}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */

function ChannelRow({
  card, onSetupLogin,
}: { card: ChannelCard; onSetupLogin: () => void; }) {
  return (
    <div
      className="border rounded p-3 flex items-start gap-3"
      style={{ borderColor: "var(--rule)", background: "var(--bg)" }}>
      <div className="pt-0.5">
        {card.requires_auth
          ? <Lock size={15} className="text-[var(--fg-muted)]" />
          : <Unlock size={15} className="text-[var(--fg-faint)]" />}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <div className="font-display text-[16px] leading-tight">{card.channel_name}</div>
          {card.language && (
            <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">
              {card.language}
            </span>
          )}
          <StateChip state={card.onboarding_state} />
          {card.requires_auth && <AuthHealthChip card={card} />}
          {card.no_publish_today && (
            <span className="font-mono text-[10px] px-1.5 py-0.5 rounded"
                  style={{ background: "var(--bg-muted, #f4f0e6)", color: "var(--fg-muted)" }}>
              non-publishing day
            </span>
          )}
        </div>

        {/* Signals — different density per state */}
        {card.onboarding_state === "crawling" ? (
          <div className="mt-2 grid grid-cols-[auto_auto_auto_auto] gap-x-6 gap-y-1 font-mono text-[11px]">
            <Signal icon={<Activity size={11} />} label="today"
                    value={`${card.today_issues_ok} iss · ${card.today_pages}p`} />
            <Signal icon={<DeltaIcon delta={card.today_delta_pct} />} label="vs 7d median"
                    value={
                      card.median_pages_7d == null ? "—"
                      : card.today_delta_pct == null ? `${card.median_pages_7d}p median`
                      : `${card.today_delta_pct >= 0 ? "+" : ""}${card.today_delta_pct}% (med ${card.median_pages_7d}p)`
                    }
                    tone={
                      card.today_delta_pct != null && card.today_delta_pct < -40 ? "danger"
                      : card.today_delta_pct != null && card.today_delta_pct < -20 ? "warn"
                      : undefined
                    } />
            <Signal icon={<Calendar size={11} />} label="last ok"
                    value={
                      card.last_ok_business_date
                        ? card.days_since_last_ok === 0
                          ? "today"
                          : `${card.days_since_last_ok}d ago (${shortDate(card.last_ok_business_date)})`
                        : "never"
                    }
                    tone={
                      !card.no_publish_today && card.days_since_last_ok != null && card.days_since_last_ok >= 3
                        ? "danger" : undefined
                    } />
            <Signal icon={<CheckCircle2 size={11} />} label="reverify"
                    value={card.reverify_status ?? "—"}
                    tone={
                      card.reverify_status === "ok" ? "ok"
                      : (card.reverify_status === "drift" || card.reverify_status === "fail") ? "warn"
                      : undefined
                    } />
            {card.requires_auth && (
              <Signal icon={<KeyRound size={11} />} label="cookies"
                      value={card.auth_cookies_expire_in_days == null ? "no expiry" : `${card.auth_cookies_expire_in_days}d left`}
                      tone={
                        card.auth_cookies_expire_in_days != null && card.auth_cookies_expire_in_days < 3 ? "danger"
                        : card.auth_cookies_expire_in_days != null && card.auth_cookies_expire_in_days < 14 ? "warn"
                        : undefined
                      } />
            )}
            <Signal icon={<Minus size={11} />} label="editions"
                    value={`${card.editions_active} / ${card.editions_total}`} />
          </div>
        ) : (
          /* Compact line for non-crawling rows */
          <div className="mt-1 font-mono text-[11px] text-[var(--fg-muted)]">
            {card.editions_total} edition{card.editions_total !== 1 ? "s" : ""} in catalog
            {card.editions_active > 0 && ` · ${card.editions_active} active`}
            {card.auth_status && ` · auth ${card.auth_status}`}
            {card.login_url && (
              <span className="text-[var(--fg-faint)]">
                {" · "}<span className="underline-offset-2">{new URL(card.login_url).hostname}</span>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-1.5 items-stretch shrink-0">
        {/* Crawling: re-login (if auth) + run (placeholder) */}
        {card.onboarding_state === "crawling" && card.requires_auth && (
          <Link
            to={`/crawler-ops/sessions?channel=${card.channel_id}`}
            className="px-3 py-1.5 border rounded font-mono text-[11px] text-center hover:bg-[var(--bg-muted,#f4f0e6)]"
            style={{ borderColor: "var(--rule)" }}>
            <KeyRound size={11} className="inline mr-1 align-text-top" /> Re-login
          </Link>
        )}
        {card.onboarding_state === "crawling" && (
          <button
            disabled
            title="Coming next: run this channel's editions on demand"
            className="px-3 py-1.5 border rounded font-mono text-[11px] opacity-40 cursor-not-allowed"
            style={{ borderColor: "var(--rule)" }}>
            ▶ Run
          </button>
        )}

        {/* Auth ready: navigate (augment) + run */}
        {card.onboarding_state === "auth_ready" && (
          <Link
            to={`/crawler-ops/sessions?channel=${card.channel_id}`}
            className="px-3 py-1.5 border rounded font-mono text-[11px] text-center hover:bg-[var(--bg-muted,#f4f0e6)]"
            style={{ borderColor: "var(--rule)" }}>
            ↩ Navigate
          </Link>
        )}

        {/* Auth partial: login (capture session) */}
        {card.onboarding_state === "auth_partial" && (
          <Link
            to={`/crawler-ops/sessions?channel=${card.channel_id}`}
            className="px-3 py-1.5 border rounded font-mono text-[11px] text-center bg-[var(--fg)] text-[var(--bg)] hover:opacity-90"
            style={{ borderColor: "var(--fg)" }}>
            <KeyRound size={11} className="inline mr-1 align-text-top" /> Login
          </Link>
        )}

        {/* Catalog: set up login (create profile) */}
        {card.onboarding_state === "catalog" && (
          <button
            onClick={onSetupLogin}
            className="px-3 py-1.5 border rounded font-mono text-[11px] hover:bg-[var(--bg-muted,#f4f0e6)]"
            style={{ borderColor: "var(--rule)" }}>
            <Plus size={11} className="inline mr-1 align-text-top" /> Set up login
          </button>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────── */

function StateChip({ state }: { state: string }) {
  type Meta = { label: string; bg: string; fg: string };
  const FALLBACK: Meta = { label: "catalog", bg: "var(--bg-muted, #f4f0e6)", fg: "var(--fg-muted)" };
  const META: Record<string, Meta> = {
    crawling:     { label: "crawling",      bg: "var(--bg-ok, #ecfdf5)",   fg: "var(--fg-ok, #15803d)" },
    auth_ready:   { label: "auth captured", bg: "var(--bg-info, #eff6ff)", fg: "var(--fg-info, #1d4ed8)" },
    auth_partial: { label: "auth partial",  bg: "var(--bg-warn, #fef3c7)", fg: "var(--fg-warn, #b45309)" },
    catalog:      FALLBACK,
  };
  const m: Meta = META[state] ?? FALLBACK;
  return (
    <span className="font-mono text-[10px] px-1.5 py-0.5 rounded"
          style={{ background: m.bg, color: m.fg }}>
      {m.label}
    </span>
  );
}

function AuthHealthChip({ card }: { card: ChannelCard }) {
  const status = card.auth_status ?? "unknown";
  const tone =
    status === "ok"      ? { bg: "var(--bg-ok, #ecfdf5)",   fg: "var(--fg-ok, #15803d)" } :
    status === "failing" ? { bg: "var(--bg-danger, #fef2f2)", fg: "var(--fg-danger)" } :
                           { bg: "var(--bg-muted, #f4f0e6)", fg: "var(--fg-muted)" };
  return (
    <span className="font-mono text-[10px] px-1.5 py-0.5 rounded"
          style={{ background: tone.bg, color: tone.fg }}>
      auth {status}
    </span>
  );
}

function Signal({
  icon, label, value, tone,
}: {
  icon: React.ReactNode; label: string; value: string;
  tone?: "ok" | "warn" | "danger";
}) {
  const color =
    tone === "danger" ? "var(--fg-danger)" :
    tone === "warn"   ? "var(--fg-warn, #b45309)" :
    tone === "ok"     ? "var(--fg-ok, #15803d)" :
    "var(--fg-muted)";
  return (
    <div className="flex items-center gap-1.5" style={{ color }}>
      <span className="opacity-70">{icon}</span>
      <span className="text-[var(--fg-faint)]">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function DeltaIcon({ delta }: { delta: number | null }) {
  if (delta == null) return <Minus size={11} />;
  if (delta > 5) return <TrendingUp size={11} />;
  if (delta < -5) return <TrendingDown size={11} />;
  return <Minus size={11} />;
}

/* ──────────────────────────────────────────────────────────────────── */

function SetupLoginModal({
  channel, onClose, onSuccess,
}: {
  channel: ChannelCard;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const qc = useQueryClient();
  const [loginUrl, setLoginUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () => createAuthProfile(channel.channel_id, loginUrl.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["crawler/categories"] });
      onSuccess();
    },
    onError: (err) => setError(explainError(err)),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!loginUrl.trim() || !loginUrl.startsWith("http")) {
      setError("Enter a full URL starting with http(s)://");
      return;
    }
    m.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center"
         onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[var(--bg)] border rounded p-5 max-w-md w-full mx-4"
        style={{ borderColor: "var(--rule)" }}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">
              Set up login
            </div>
            <h2 className="font-display text-[20px] leading-tight">{channel.channel_name}</h2>
            <div className="font-mono text-[11px] text-[var(--fg-muted)] mt-0.5">
              {channel.category} · {channel.language ?? "—"} · {channel.editions_total} editions
            </div>
          </div>
          <button onClick={onClose} aria-label="close">
            <XIcon size={18} className="text-[var(--fg-muted)]" />
          </button>
        </div>

        <p className="font-sans text-[13px] text-[var(--fg-muted)] mb-3">
          Creates an AuthProfile for this publisher so a team member can log in and
          capture cookies. Once created, the channel moves to <em>auth partial</em> and
          a <strong>Login</strong> button appears.
        </p>

        <form onSubmit={submit} className="space-y-2.5">
          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">
              Login URL
            </div>
            <input
              type="url"
              value={loginUrl}
              onChange={(e) => setLoginUrl(e.target.value)}
              placeholder="https://epaper.example.com/login"
              autoFocus
              className="w-full border rounded px-2 py-1.5 font-mono text-[12px] bg-transparent"
              style={{ borderColor: "var(--rule)" }}
            />
            <div className="font-mono text-[10px] text-[var(--fg-faint)] mt-1">
              The publisher's login page. The browser will open this in the Sessions VNC.
            </div>
          </label>
          {error && (
            <div className="font-sans text-[12px]" style={{ color: "var(--fg-danger)" }}>
              <AlertTriangle size={12} className="inline mr-1 align-text-bottom" />
              {error}
            </div>
          )}
          <div className="flex gap-2 justify-end pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 border rounded font-mono text-[11px] hover:bg-[var(--bg-muted,#f4f0e6)]"
              style={{ borderColor: "var(--rule)" }}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={m.isPending || !loginUrl.trim()}
              className="px-3 py-1.5 border rounded font-mono text-[11px] bg-[var(--fg)] text-[var(--bg)] disabled:opacity-50"
              style={{ borderColor: "var(--fg)" }}>
              {m.isPending ? <Spinner /> : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
